import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/widgets";
import { useFreshness } from "@/context/FreshnessContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import dayjs from "dayjs";
import {
  AlertTriangle, CheckCircle2, Download, FileSpreadsheet, FileWarning, Loader2,
  MapPin, ShieldCheck, Trash2, Undo2, UploadCloud, X,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OemUploadTab } from "@/components/oem/OemUploadTab";
import { cn } from "@/lib/utils";

const STEPS = ["Select File", "Map Columns", "Preview & Confirm", "Complete"];

const BatchStatusBadge = ({ status }) => (
  <Badge
    variant="outline"
    className={cn(
      "text-[0.65rem] uppercase tracking-wider",
      status === "committed"
        ? "text-[hsl(var(--status-green))] border-[hsl(var(--status-green)/0.35)] bg-[hsl(var(--status-green)/0.08)]"
        : "text-muted-foreground bg-[hsl(var(--cx-surface-2))]"
    )}
    data-testid="batch-status-badge"
  >
    {status === "rolled_back" ? "Rolled back" : "Committed"}
  </Badge>
);

export default function UploadPage() {
  return (
    <div data-testid="upload-page-wrapper">
      <Tabs defaultValue="complaints" data-testid="upload-tabs">
        <TabsList>
          <TabsTrigger value="complaints" data-testid="upload-tab-complaints">Complaint Dump</TabsTrigger>
          <TabsTrigger value="oem" data-testid="upload-tab-oem">OEM CX Scorecards</TabsTrigger>
        </TabsList>
        <TabsContent value="complaints" className="mt-4"><ComplaintUploadFlow /></TabsContent>
        <TabsContent value="oem" className="mt-4">
          <PageHeader title="OEM CX Scorecard Upload"
            subtitle="Kia's monthly CX KPI Evaluation per dealer code · parse → preview → confirm · every batch can be undone" />
          <OemUploadTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ComplaintUploadFlow() {
  const { refresh: refreshFreshness } = useFreshness();
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [sheetConfigs, setSheetConfigs] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [rollbackTarget, setRollbackTarget] = useState(null); // batch or "last"
  const [cascadeInfo, setCascadeInfo] = useState(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [missingReviewId, setMissingReviewId] = useState(null);
  const inputRef = useRef();

  const loadHistory = useCallback(() => {
    api.get("/imports").then((r) => setHistory(r.data)).catch((e) => console.error("Background fetch failed:", e));
  }, []);
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const parseFile = async (f) => {
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/imports/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(r.data);
      setSheetConfigs(
        r.data.sheets.map((s) => ({
          name: s.name,
          function: s.detected_function || "auto",
          mapping: { ...s.suggested_mapping },
          skip: false,
        }))
      );
      setStep(1);
    } catch (e) {
      toast.error(errMsg(e));
      setFile(null);
    } finally {
      setParsing(false);
    }
  };

  const onFile = (f) => {
    if (!f) return;
    setFile(f);
    parseFile(f);
  };

  const setMapping = (sheetIdx, fieldKey, header) => {
    const value = header === "__none__" ? null : header;
    setSheetConfigs((prev) =>
      prev.map((s, i) =>
        i === sheetIdx ? { ...s, mapping: { ...s.mapping, [fieldKey]: value } } : s
      )
    );
  };

  const buildBody = () => ({
    upload_id: parsed.upload_id,
    sheets: sheetConfigs.map((s) => ({
      name: s.name,
      function: s.function === "auto" ? null : s.function,
      mapping: s.mapping,
      skip: s.skip,
    })),
  });

  const validateMapping = () => {
    for (const cfg of sheetConfigs.filter((s) => !s.skip)) {
      for (const f of parsed.system_fields.filter((x) => x.required)) {
        if (!cfg.mapping[f.key]) {
          toast.error(`Sheet "${cfg.name}": map required field "${f.label}"`);
          return false;
        }
      }
    }
    return true;
  };

  const runPreview = async () => {
    if (!validateMapping()) return;
    setPreviewing(true);
    try {
      const r = await api.post("/imports/preview", buildBody());
      setPreview(r.data);
      setStep(2);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await api.post("/imports/commit", buildBody());
      setResult(r.data);
      setStep(3);
      loadHistory();
      refreshFreshness();
      toast.success("Import committed — undo is available in Batch History");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCommitting(false);
    }
  };

  const doRollback = async (cascadeConfirmed = false) => {
    setRollingBack(true);
    try {
      let r;
      if (rollbackTarget === "last") {
        r = await api.post("/imports/rollback-last");
      } else {
        r = await api.post(`/imports/${rollbackTarget.id}/rollback`, { cascade_confirmed: cascadeConfirmed });
        if (r.data.requires_cascade) {
          setCascadeInfo(r.data);
          setRollingBack(false);
          return;
        }
      }
      const results = r.data.rolled_back || [];
      const deleted = results.reduce((s, x) => s + (x.deleted || 0), 0);
      const restored = results.reduce((s, x) => s + (x.restored || 0), 0);
      toast.success(`Rollback complete — ${deleted} complaint(s) removed, ${restored} restored to prior state`);
      setRollbackTarget(null);
      setCascadeInfo(null);
      loadHistory();
      refreshFreshness();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setRollingBack(false);
    }
  };

  const downloadErrors = async (importId) => {
    try {
      const r = await api.get(`/imports/${importId}/errors.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `import_errors.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const reset = () => {
    setStep(0);
    setFile(null);
    setParsed(null);
    setPreview(null);
    setResult(null);
    setSheetConfigs([]);
  };

  const lastCommitted = history.find((h) => h.status !== "rolled_back");
  const highOverlap = preview && preview.overlap.pct >= 50;

  const onMissingRemoved = (importId, remaining) => {
    loadHistory();
    refreshFreshness();
    if (result?.id === importId) {
      setResult((r) => ({ ...r, missing_count: remaining }));
    }
  };

  return (
    <div data-testid="upload-page">
      <PageHeader
        title="Upload Salesforce Dump"
        subtitle="Parse → map → preview → confirm · nothing is written until you confirm · every upload can be undone"
      />

      <Stepper step={step} />

      {step === 0 && <FileSelectCard parsing={parsing} file={file} inputRef={inputRef} onFile={onFile} />}

      {step === 1 && parsed && (
        <MappingStep parsed={parsed} sheetConfigs={sheetConfigs} setSheetConfigs={setSheetConfigs}
          setMapping={setMapping} reset={reset} runPreview={runPreview} previewing={previewing} />
      )}

      {step === 2 && preview && (
        <PreviewStep preview={preview} highOverlap={highOverlap} reset={reset}
          setStep={setStep} commit={commit} committing={committing} />
      )}

      {step === 3 && result && <ResultStep result={result} reset={reset} downloadErrors={downloadErrors}
        openMissingReview={setMissingReviewId} />}

      <MissingDigestBanner history={history} openMissingReview={setMissingReviewId} />

      <BatchHistoryCard history={history} lastCommitted={lastCommitted}
        setRollbackTarget={setRollbackTarget} downloadErrors={downloadErrors}
        openMissingReview={setMissingReviewId} />

      <MissingReviewDialog importId={missingReviewId}
        onOpenChange={(v) => !v && setMissingReviewId(null)} onRemoved={onMissingRemoved} />

      <RollbackConfirmDialog rollbackTarget={rollbackTarget} cascadeInfo={cascadeInfo}
        lastCommitted={lastCommitted} setRollbackTarget={setRollbackTarget}
        doRollback={doRollback} rollingBack={rollingBack} />

      <CascadeDialog cascadeInfo={cascadeInfo} setCascadeInfo={setCascadeInfo}
        setRollbackTarget={setRollbackTarget} doRollback={doRollback} rollingBack={rollingBack} />
    </div>
  );
}

/* ---------- Upload flow step components (extracted for readability/testability) ---------- */

const stepChipClass = (i, step) => {
  if (i === step) return "bg-[hsl(var(--cx-navy))] text-white border-transparent";
  if (i < step) return "bg-[hsl(var(--accent))] text-[hsl(var(--cx-navy))] border-[hsl(var(--cx-gold)/0.4)]";
  return "text-muted-foreground";
};

const Stepper = ({ step }) => (
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${stepChipClass(i, step)}`}>
              <span className="tabular">{i + 1}</span> {s}
            </div>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-border" />}
          </div>
        ))}
      </div>
);

const FileSelectCard = ({ parsing, file, inputRef, onFile }) => (
        <Card>
          <CardContent className="py-10">
            <button
              className="w-full border-2 border-dashed rounded-xl py-14 flex flex-col items-center gap-3 hover:bg-[hsl(var(--cx-surface))] transition-colors"
              onClick={() => inputRef.current?.click()}
              data-testid="upload-dropzone-button"
            >
              {parsing ? (
                <>
                  <Loader2 size={36} className="animate-spin text-[hsl(var(--cx-navy))]" />
                  <p className="text-sm text-muted-foreground">Parsing {file?.name}…</p>
                </>
              ) : (
                <>
                  <UploadCloud size={36} className="text-[hsl(var(--cx-navy))]" />
                  <div className="text-center">
                    <p className="font-medium">Click to select the Salesforce dump</p>
                    <p className="text-xs text-muted-foreground mt-1">.xlsx or .csv · up to 30 MB · multi-sheet supported</p>
                  </div>
                </>
              )}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv,.xls"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="upload-file-input"
            />
          </CardContent>
        </Card>
);

const MappingStep = ({ parsed, sheetConfigs, setSheetConfigs, setMapping, reset, runPreview, previewing }) => (
        <div className="space-y-4">
          <Alert>
            <AlertDescription className="text-sm">
              <FileSpreadsheet size={14} className="inline mr-1.5" />
              <strong>{parsed.filename}</strong> — {parsed.sheets.length} sheet(s) detected. Confirm the function and column mapping for each sheet.
            </AlertDescription>
          </Alert>
          {parsed.sheets.map((sheet, si) => (
            <Card key={sheet.name}>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="font-serif text-base font-normal">
                    {sheet.name} <span className="text-xs text-muted-foreground">({sheet.row_count.toLocaleString()} rows)</span>
                    {sheet.mapping_was_saved && (
                      <Badge variant="outline" className="ml-2 text-[0.6rem] bg-[hsl(var(--accent))]">saved mapping</Badge>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Function:</span>
                    <Select value={sheetConfigs[si]?.function}
                      onValueChange={(v) => setSheetConfigs((p) => p.map((s, i) => (i === si ? { ...s, function: v } : s)))}>
                      <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`upload-sheet-function-select-${si}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sales">Sales</SelectItem>
                        <SelectItem value="Service">Service</SelectItem>
                        <SelectItem value="auto">Auto (by department)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" className="h-8 text-xs"
                      onClick={() => setSheetConfigs((p) => p.map((s, i) => (i === si ? { ...s, skip: !s.skip } : s)))}
                      data-testid={`upload-sheet-skip-button-${si}`}>
                      {sheetConfigs[si]?.skip ? "Include" : "Skip sheet"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {!sheetConfigs[si]?.skip && (
                <CardContent className="pt-0">
                  <div className="overflow-x-auto">
                    <Table className="cx-table" data-testid={`upload-mapping-table-${si}`}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>System Field</TableHead>
                          <TableHead>File Column</TableHead>
                          <TableHead className="hidden md:table-cell">Sample</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parsed.system_fields.map((f) => {
                          const mapped = sheetConfigs[si]?.mapping?.[f.key];
                          const colIdx = sheet.headers.indexOf(mapped);
                          return (
                            <TableRow key={f.key}>
                              <TableCell className="whitespace-nowrap">
                                {f.label}
                                {f.required && <span className="text-[hsl(var(--cx-gold))] ml-1">*</span>}
                              </TableCell>
                              <TableCell>
                                <Select value={mapped || "__none__"} onValueChange={(v) => setMapping(si, f.key, v)}>
                                  <SelectTrigger className="h-8 w-[220px] text-xs" data-testid={`upload-mapping-select-${si}-${f.key}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-60">
                                    <SelectItem value="__none__">— not mapped —</SelectItem>
                                    {sheet.headers.map((h) => (
                                      <SelectItem key={h} value={h}>{h}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="hidden md:table-cell text-xs text-muted-foreground max-w-[280px] truncate">
                                {colIdx >= 0 ? sheet.sample_rows.map((r) => r[colIdx]).filter(Boolean).slice(0, 2).join(" · ") : ""}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
          <div className="flex justify-between">
            <Button variant="outline" onClick={reset} data-testid="upload-back-button">
              <X size={15} className="mr-1.5" /> Cancel
            </Button>
            <Button onClick={runPreview} disabled={previewing} className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="upload-preview-button">
              {previewing ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <ShieldCheck size={15} className="mr-1.5" />}
              Preview Import
            </Button>
          </div>
        </div>
);

const PreviewStep = ({ preview, highOverlap, reset, setStep, commit, committing }) => (
        <div className="space-y-4" data-testid="upload-preview-panel">
          <Alert className="border-[hsl(var(--cx-navy)/0.25)] bg-[hsl(var(--cx-surface))]">
            <AlertTitle className="font-serif text-base">Safety preview — nothing has been written yet</AlertTitle>
            <AlertDescription className="text-sm">
              Review the analysis below. Complaints are only created or updated after you press <strong>Confirm Import</strong>.
            </AlertDescription>
          </Alert>

          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  ["Rows in file", preview.total_rows, "preview-total-rows"],
                  ["Valid rows", preview.valid_rows, "preview-valid-rows"],
                  ["Will be created", preview.estimated_new, "preview-estimated-new"],
                  ["Will be updated", preview.estimated_updates, "preview-estimated-updates"],
                  ["Row errors", preview.row_error_count, "preview-row-errors"],
                ].map(([label, val, tid]) => (
                  <div key={label} className="border rounded-lg p-3 text-center" data-testid={tid}>
                    <p className="kpi-value text-2xl">{val}</p>
                    <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {preview.overlap.existing > 0 && (
            <Alert
              data-testid="preview-overlap-warning"
              className={cn(highOverlap && "border-[hsl(var(--status-amber)/0.5)] bg-[hsl(var(--status-amber)/0.06)]")}
            >
              <AlertTriangle size={16} className={highOverlap ? "text-[hsl(var(--status-amber))]" : "text-[hsl(var(--cx-navy))]"} />
              <AlertTitle className="text-sm font-semibold">
                {highOverlap ? "High overlap with existing data" : "Overlap with existing data"}
              </AlertTitle>
              <AlertDescription className="text-sm">
                {preview.overlap.existing.toLocaleString()} of {preview.overlap.total.toLocaleString()} complaint numbers
                ({preview.overlap.pct}%) already exist — they will be <strong>updated</strong>, not duplicated.
                {" "}File covers {preview.overlap.date_from} to {preview.overlap.date_to}.
                {highOverlap && " If this looks like a repeated upload of yesterday's dump, cancel and verify the file."}
              </AlertDescription>
            </Alert>
          )}

          {preview.mapping_error_count > 0 && (
            <Card className="border-[hsl(var(--cx-crimson)/0.35)]" data-testid="preview-mapping-errors">
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-base font-normal text-[hsl(var(--cx-crimson))] flex items-center gap-2">
                  <AlertTriangle size={16} /> Location mapping issues ({preview.mapping_error_count})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-0">
                <p className="text-xs text-muted-foreground px-5 pb-2">
                  These rows point at inactive locations or the wrong function for the location type. They will still import —
                  fix the Location Master in Settings or correct the data in Salesforce.
                </p>
                <div className="max-h-56 overflow-y-auto">
                  <Table className="cx-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pl-4">Complaint</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Function</TableHead>
                        <TableHead className="pr-4">Issue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.mapping_errors.map((m) => (
                        <TableRow key={`${m.complaint_number}-${m.issue}`}>
                          <TableCell className="pl-4 tabular">{m.complaint_number}</TableCell>
                          <TableCell>{m.outlet}</TableCell>
                          <TableCell>{m.function}</TableCell>
                          <TableCell className="pr-4 text-xs text-[hsl(var(--cx-crimson))]">{m.issue}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} data-testid="upload-cancel-button">
                <X size={15} className="mr-1.5" /> Cancel upload
              </Button>
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="upload-back-to-mapping-button">
                Back to mapping
              </Button>
            </div>
            <Button onClick={commit} disabled={committing} className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="upload-confirm-button">
              {committing ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <CheckCircle2 size={15} className="mr-1.5" />}
              Confirm Import
            </Button>
          </div>
        </div>
);

/* Warns the admin when this upload auto-created brand-new locations: they
   default to "Sales + Service" and should have their type set immediately so
   Sales/Service dashboards stay accurate. */
const NewLocationsAlert = ({ outlets }) => {
  const navigate = useNavigate();
  if (!outlets?.length) return null;
  const shown = outlets.slice(0, 6);
  return (
    <Alert className="mt-3 border-[hsl(var(--cx-gold)/0.55)] bg-[hsl(var(--cx-gold)/0.07)]" data-testid="new-locations-alert">
      <MapPin className="h-4 w-4 text-[hsl(var(--cx-gold))]" />
      <AlertTitle className="text-sm">
        {outlets.length} new location{outlets.length !== 1 ? "s" : ""} auto-created from this dump
      </AlertTitle>
      <AlertDescription className="text-xs">
        <span className="block">
          {shown.map((o) => `${o.code} ${o.name}`).join(" · ")}{outlets.length > shown.length ? ` · +${outlets.length - shown.length} more` : ""}
        </span>
        New locations default to <strong>Sales + Service</strong>. If any is Sales-only or Service-only,
        set its type now so it appears in the right dashboards and leaderboards.
        <div className="mt-2">
          <Button variant="outline" size="sm" className="h-8 text-xs"
            onClick={() => navigate("/settings?tab=outlets")} data-testid="new-locations-review-button">
            <MapPin size={13} className="mr-1.5" /> Set location types
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};

/* Persistent reminder: unreviewed missing-from-dump complaints across all
   committed batches. Clears automatically as reviews are completed. */
const MissingDigestBanner = ({ history, openMissingReview }) => {
  const pending = history.filter((h) => h.status !== "rolled_back" && h.missing_count > 0);
  const total = pending.reduce((s, h) => s + h.missing_count, 0);
  if (total === 0) return null;
  return (
    <Alert className="mb-5 border-[hsl(var(--status-amber)/0.5)] bg-[hsl(var(--status-amber)/0.07)]" data-testid="missing-digest-banner">
      <FileWarning className="h-4 w-4 text-[hsl(var(--status-amber))]" />
      <AlertTitle className="text-sm" data-testid="missing-digest-count">
        {total} complaint{total !== 1 ? "s" : ""} pending deletion review
      </AlertTitle>
      <AlertDescription className="text-xs">
        Across {pending.length} upload{pending.length !== 1 ? "s" : ""} — these exist in the portal but were
        missing from their dump's date range. Review them so the portal matches Salesforce.
        <div className="mt-2">
          <Button variant="outline" size="sm" className="h-8 text-xs"
            onClick={() => openMissingReview(pending[0].id)} data-testid="missing-digest-review-button">
            <FileWarning size={13} className="mr-1.5" /> Review now
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};

const ResultStep = ({ result, reset, downloadErrors, openMissingReview }) => (
        <div className="space-y-4" data-testid="upload-summary-panel">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg font-normal flex items-center gap-2">
                <CheckCircle2 size={20} className="text-[hsl(var(--status-green))]" /> Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  ["New", result.summary.new, "upload-summary-new"],
                  ["Updated", result.summary.updated, "upload-summary-updated"],
                  ["Unchanged", result.summary.unchanged, "upload-summary-unchanged"],
                  ["Closed since last", result.summary.closed_now, "upload-summary-closed"],
                  ["Errors", result.error_count, "upload-summary-errors"],
                ].map(([label, val, tid]) => (
                  <div key={label} className="border rounded-lg p-3 text-center" data-testid={tid}>
                    <p className="kpi-value text-2xl">{val}</p>
                    <p className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
              {result.missing_count > 0 && (
                <Alert className="mt-4 border-[hsl(var(--status-amber)/0.5)] bg-[hsl(var(--status-amber)/0.07)]" data-testid="missing-review-alert">
                  <FileWarning className="h-4 w-4 text-[hsl(var(--status-amber))]" />
                  <AlertTitle className="text-sm">
                    {result.missing_count} complaint{result.missing_count !== 1 ? "s" : ""} in the portal {result.missing_count !== 1 ? "were" : "was"} not found in this dump
                  </AlertTitle>
                  <AlertDescription className="text-xs">
                    Within this dump's date range{result.missing_range ? ` (${result.missing_range.from} – ${result.missing_range.to})` : ""}.
                    If they were deleted in Salesforce, review and remove them so the portal matches the source.
                    <div className="mt-2">
                      <Button variant="outline" size="sm" className="h-8 text-xs"
                        onClick={() => openMissingReview(result.id)} data-testid="missing-review-open-button">
                        <FileWarning size={13} className="mr-1.5" /> Review missing complaints
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
              <NewLocationsAlert outlets={result.new_outlets} />
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
                <Undo2 size={13} className="text-[hsl(var(--cx-gold))]" />
                Uploaded by mistake? Use <strong>Rollback Last Upload</strong> in Batch History below — it removes rows this
                upload created and restores rows it changed.
              </p>
              {result.error_count > 0 && (
                <Button variant="outline" className="mt-4" onClick={() => downloadErrors(result.id)} data-testid="upload-download-errors-button">
                  <Download size={14} className="mr-1.5" /> Download error file ({result.error_count} rows)
                </Button>
              )}
              <div className="mt-4 flex gap-2">
                <Button onClick={reset} variant="outline" data-testid="upload-another-button">Upload another file</Button>
              </div>
            </CardContent>
          </Card>
        </div>
);

const BatchHistoryCard = ({ history, lastCommitted, setRollbackTarget, downloadErrors, openMissingReview }) => (
      <Card className="mt-6">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="font-serif text-base font-normal">Batch History</CardTitle>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs border-[hsl(var(--cx-crimson)/0.4)] text-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson)/0.06)]"
              disabled={!lastCommitted || !lastCommitted.undo_available}
              onClick={() => setRollbackTarget("last")}
              data-testid="rollback-last-upload-button"
            >
              <Undo2 size={13} className="mr-1.5" /> Rollback Last Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0 px-0">
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground px-5 pb-4">No imports yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table className="cx-table min-w-[860px]" data-testid="import-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-4">File</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>When</TableHead>
                    <TableHead className="text-right">New</TableHead>
                    <TableHead className="text-right">Updated</TableHead>
                    <TableHead className="text-right">Closed</TableHead>
                    <TableHead className="text-right">Overlap</TableHead>
                    <TableHead className="text-right">Errors</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="pr-4"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id} className={h.status === "rolled_back" ? "opacity-55" : ""} data-testid={`batch-row-${h.id.slice(0, 8)}`}>
                      <TableCell className="pl-4 max-w-[240px]">
                        <span className="block truncate">{h.filename}</span>
                        {h.status !== "rolled_back" && h.missing_count > 0 && (
                          <button
                            className="mt-0.5 inline-flex items-center gap-1 text-[0.65rem] font-medium text-[hsl(var(--status-amber))] underline underline-offset-2"
                            onClick={() => openMissingReview(h.id)}
                            data-testid={`batch-missing-review-${h.id.slice(0, 8)}`}
                          >
                            <FileWarning size={11} /> {h.missing_count} missing from dump — review
                          </button>
                        )}
                      </TableCell>
                      <TableCell>{h.by}</TableCell>
                      <TableCell className="whitespace-nowrap tabular text-xs">{dayjs(h.at).format("DD MMM YY, HH:mm")}</TableCell>
                      <TableCell className="text-right tabular">{h.summary?.new}</TableCell>
                      <TableCell className="text-right tabular">{h.summary?.updated}</TableCell>
                      <TableCell className="text-right tabular">{h.summary?.closed_now}</TableCell>
                      <TableCell className="text-right tabular text-xs">{h.overlap ? `${h.overlap.pct}%` : "—"}</TableCell>
                      <TableCell className="text-right tabular">
                        {h.error_count > 0 ? (
                          <button className="underline text-[hsl(var(--cx-crimson))]" onClick={() => downloadErrors(h.id)}>
                            {h.error_count}
                          </button>
                        ) : (
                          0
                        )}
                      </TableCell>
                      <TableCell><BatchStatusBadge status={h.status || "committed"} /></TableCell>
                      <TableCell className="pr-4 text-right">
                        {h.status !== "rolled_back" && (
                          h.undo_available ? (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-[hsl(var(--cx-crimson))]"
                              onClick={() => setRollbackTarget(h)}
                              data-testid={`batch-rollback-button-${h.id.slice(0, 8)}`}>
                              <Undo2 size={12} className="mr-1" /> Rollback
                            </Button>
                          ) : (
                            <span className="text-[0.65rem] text-muted-foreground uppercase tracking-wider">No undo</span>
                          )
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
);

/* Deletion-sync review: complaints present in the portal but absent from the
   uploaded dump (within its date range). Admin explicitly confirms removal;
   every removal is a rollback-able batch. */
const MissingReviewDialog = ({ importId, onOpenChange, onRemoved }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!importId) return;
    setLoading(true);
    setSelected(new Set());
    setConfirming(false);
    api.get(`/imports/${importId}/missing`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [importId]);

  const items = data?.items || [];
  const allSelected = items.length > 0 && selected.size === items.length;

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(items.map((c) => c.complaint_number)));
    setConfirming(false);
  };
  const toggleOne = (num) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(num)) n.delete(num); else n.add(num);
      return n;
    });
    setConfirming(false);
  };

  const doRemove = async () => {
    setRemoving(true);
    try {
      const r = await api.post(`/imports/${importId}/remove-missing`, {
        complaint_numbers: Array.from(selected),
      });
      toast.success(`${r.data.removed} complaint(s) removed — undo available in Batch History`);
      onRemoved(importId, r.data.remaining_missing);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setRemoving(false);
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="space-y-2 py-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={`missing-skeleton-${i}`} className="h-9" />)}</div>;
    }
    if (items.length === 0) {
      return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="missing-review-empty">Nothing left to review — all flagged complaints have been handled.</p>;
    }
    return (
      <div className="border rounded-md overflow-hidden">
        <div className="max-h-[46vh] overflow-y-auto">
          <Table className="cx-table" data-testid="missing-review-table">
            <TableHeader>
              <TableRow>
                <TableHead className="pl-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" data-testid="missing-select-all" />
                </TableHead>
                <TableHead>Complaint</TableHead>
                <TableHead>Outlet</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="pr-3">Reported</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.complaint_number} data-testid={`missing-row-${c.complaint_number}`}>
                  <TableCell className="pl-3">
                    <Checkbox checked={selected.has(c.complaint_number)}
                      onCheckedChange={() => toggleOne(c.complaint_number)}
                      aria-label={`Select ${c.complaint_number}`}
                      data-testid={`missing-row-checkbox-${c.complaint_number}`} />
                  </TableCell>
                  <TableCell className="font-medium whitespace-nowrap">{c.complaint_number}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{c.outlet_code} {c.outlet_name}</TableCell>
                  <TableCell className="max-w-[140px] truncate text-xs">{c.category || "—"}</TableCell>
                  <TableCell className="text-xs">{c.status}</TableCell>
                  <TableCell className="pr-3 tabular text-xs whitespace-nowrap">{c.date_reported_fmt || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={Boolean(importId)} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="missing-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif font-normal flex items-center gap-2">
            <FileWarning size={18} className="text-[hsl(var(--status-amber))]" /> Missing from dump
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          These complaints exist in the portal but were absent from the uploaded dump
          {data?.range ? ` (date range ${data.range.from} – ${data.range.to})` : ""}. Remove the ones that
          were deleted in Salesforce — removals can be undone from Batch History.
        </p>
        {renderBody()}
        {confirming ? (
          <div className="border border-[hsl(var(--cx-crimson)/0.4)] bg-[hsl(var(--cx-crimson)/0.05)] rounded-md p-3 flex flex-wrap items-center justify-between gap-2" data-testid="missing-confirm-strip">
            <p className="text-xs text-[hsl(var(--cx-crimson))] font-medium">
              Permanently remove {selected.size} complaint{selected.size !== 1 ? "s" : ""} from the portal? Undo stays available in Batch History.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setConfirming(false)} data-testid="missing-cancel-button">Cancel</Button>
              <Button size="sm" className="h-8 text-xs bg-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson)/0.9)] text-white"
                onClick={doRemove} disabled={removing} data-testid="missing-confirm-button">
                {removing ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Trash2 size={13} className="mr-1.5" />}
                Confirm removal
              </Button>
            </div>
          </div>
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="missing-close-button">Close</Button>
            <Button
              className="bg-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson)/0.9)] text-white"
              disabled={selected.size === 0 || items.length === 0}
              onClick={() => setConfirming(true)}
              data-testid="missing-remove-button"
            >
              <Trash2 size={14} className="mr-1.5" /> Remove selected ({selected.size})
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RollbackConfirmDialog = ({ rollbackTarget, cascadeInfo, lastCommitted, setRollbackTarget, doRollback, rollingBack }) => (
      <Dialog open={Boolean(rollbackTarget) && !cascadeInfo} onOpenChange={(v) => !v && setRollbackTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="rollback-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[hsl(var(--cx-crimson))]">
              {rollbackTarget === "last" ? "Rollback the last upload?" : `Rollback "${rollbackTarget?.filename}"?`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Complaints <strong>created</strong> by this upload will be removed and complaints it <strong>changed</strong> will
            be restored to their previous state. Aging, escalations and daily reports are recalculated automatically.
          </p>
          {rollbackTarget === "last" && lastCommitted && (
            <p className="text-sm border rounded-md px-3 py-2 bg-[hsl(var(--cx-surface))]">
              <strong>{lastCommitted.filename}</strong> · {dayjs(lastCommitted.at).format("DD MMM YY, HH:mm")} ·{" "}
              {lastCommitted.summary?.new} new / {lastCommitted.summary?.updated} updated
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRollbackTarget(null)} data-testid="rollback-cancel-button">Cancel</Button>
            <Button onClick={() => doRollback(false)} disabled={rollingBack}
              className="bg-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson))]/90 text-white"
              data-testid="rollback-confirm-button">
              {rollingBack && <Loader2 size={14} className="animate-spin mr-1.5" />} Rollback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
);

const CascadeDialog = ({ cascadeInfo, setCascadeInfo, setRollbackTarget, doRollback, rollingBack }) => (
      <Dialog open={Boolean(cascadeInfo)} onOpenChange={(v) => { if (!v) { setCascadeInfo(null); setRollbackTarget(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="rollback-cascade-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[hsl(var(--cx-crimson))] flex items-center gap-2">
              <AlertTriangle size={18} /> Newer uploads must be rolled back too
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{cascadeInfo?.message}</p>
          <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
            {(cascadeInfo?.later_batches || []).map((b) => (
              <div key={b.id} className="px-3 py-2 text-sm flex items-center justify-between gap-2">
                <span className="truncate">{b.filename}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap tabular">{dayjs(b.at).format("DD MMM YY, HH:mm")}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCascadeInfo(null); setRollbackTarget(null); }} data-testid="cascade-cancel-button">Cancel</Button>
            <Button onClick={() => doRollback(true)} disabled={rollingBack}
              className="bg-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson))]/90 text-white"
              data-testid="cascade-confirm-button">
              {rollingBack && <Loader2 size={14} className="animate-spin mr-1.5" />}
              Rollback {1 + (cascadeInfo?.later_batches?.length || 0)} batches
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
);
